import { msg } from "./Messages";
import type { UsageMeterEventName } from "./UsageMeter";

export interface MeterInfo {
  name: string;
  description: string;
  billingBasis: string;
  color: string;
  formatType: "number" | "bytes" | "tokens" | "megabyteHours";
  /** Amounts from `factor` up are written in compact notation ("2.5M"); prices are per `factor`. */
  scale?: {
    factor: number;
  };
}

export type MeterFormatType = MeterInfo["formatType"];

/** Storage is billed in megabyte-hours: one GB kept for a 730-hour month is 747,520 of them. */
export const STORAGE_HOURS_PER_MONTH = 730;
export const MB_HOURS_PER_GB_MONTH = 1024 * STORAGE_HOURS_PER_MONTH;

export const METER_INFO = {
  asset_search: {
    name: msg("AI Asset Searches"),
    description: msg("Successful natural-language asset searches"),
    billingBasis: msg(
      "Each successful semantic query, including queries with no matches; keyword, settings and docs search are free",
    ),
    color: "#a78bfa",
    formatType: "number",
  },
  asset_media_search_indexing: {
    name: msg("AI Video & Audio Indexing"),
    description: msg(
      "Preparation of audio and video for AI search in projects with media search on",
    ),
    billingBasis: msg(
      "One unit per started six seconds of audio/video, once per content revision; image and text indexing and repairs are free",
    ),
    color: "#a78bfa",
    formatType: "number",
  },
  asset_storage_mb_hours: {
    name: msg("Asset Storage"),
    description: msg("Storage your retained assets use over time"),
    billingBasis: msg(
      "Megabytes stored, measured every hour; 1 GB kept for a whole month is 1 GB-month (747,520 MB-hours)",
    ),
    color: "#82ca9d",
    formatType: "megabyteHours",
  },
  asset_read: {
    name: msg("Asset Reads"),
    description: msg("Number of times assets have been accessed or served"),
    billingBasis: msg("Each asset access or delivery"),
    color: "#ffc658",
    formatType: "number",
  },
  image_transformed: {
    name: msg("Image Transforms"),
    description: msg("Unique image variants per asset first used in billable requests"),
    billingBasis: msg("Once per asset and variant; reuse in later months adds no transform charge"),
    color: "#ff7300",
    formatType: "number",
  },
  image_classification: {
    name: msg("Image Moderation"),
    description: msg("Number of images processed for content moderation"),
    billingBasis: msg("Each image processed for moderation"),
    color: "#00c49f",
    formatType: "number",
  },
  text_classification_tokens: {
    name: msg("Text Moderation"),
    description: msg("Number of tokens processed for text content moderation"),
    billingBasis: msg(
      "Each 1 million billable text tokens processed, with a 250-token minimum per moderation request",
    ),
    color: "#8dd1e1",
    formatType: "tokens",
    scale: {
      factor: 1_000_000,
    },
  },
  website_ingestion: {
    name: msg("Website Ingestions"),
    description: msg("Number of websites processed and ingested"),
    billingBasis: msg("Each website ingested"),
    color: "#d084d0",
    formatType: "number",
  },
  ocr_classification: {
    name: msg("OCR Moderation"),
    description: msg("Number of OCR text extractions"),
    billingBasis: msg("Each OCR text extraction"),
    color: "#ec4899",
    formatType: "number",
  },
  person_detection: {
    name: msg("Person Detection"),
    description: msg("Number of images analysed for people (preview)"),
    billingBasis: msg("Each image analysed for people"),
    color: "#14b8a6",
    formatType: "number",
  },
  face_detection: {
    name: msg("Face Detection"),
    description: msg("Number of images analysed for faces, including age estimation (preview)"),
    billingBasis: msg("Each image analysed for faces"),
    color: "#f97316",
    formatType: "number",
  },
  /*multimedia_document_ingestion: {
    name: "Multimedia Document Ingestions",
    description: "Number of multimedia documents processed and ingested",
    billingBasis: "Each multimedia document ingested",
    color: "#a084d0",
    formatType: "number",
  },*/
} as const satisfies Record<UsageMeterEventName, MeterInfo>;

export const getMeterInfo = (meterKey: UsageMeterEventName): MeterInfo | undefined => {
  return METER_INFO[meterKey];
};
